'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';

import type { ExportableColumn, ExportFormat, ExportState } from '@savvy/data-table';
import { downloadWithAuth } from '@savvy/deprecated-shared/utils/download-with-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithPolling } from '@savvy/graphql-hooks/useQueryWithPolling';

import type { ClientsUrlState } from '../utils/url-state-hooks';
import type { SavedExportPreset } from './use-saved-household-export-presets';
import { CreateDataExportMutation, DataExportQuery } from './graphql';
import { useSavedHouseholdExportPresets } from './use-saved-household-export-presets';

type DeferredPromise = {
  resolve: () => void;
  reject: (err: Error) => void;
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_TIME_MS = 120_000;

// Column ids must match backend HouseholdExporter ids 1:1. Grouped into
// sections so the export modal's column picker can render grouped dropdown
// sections; group order here drives section order in the UI.
// Pre-existing columns keep their original isVisible: true default (Linear
// 4C1T-82) so advisors don't lose columns they already relied on. New columns
// default to hidden except the handful called out as visible-by-default in
// the ticket, since selecting all ~35 by default would overwhelm the export.
const HOUSEHOLD_EXPORT_COLUMNS: readonly ExportableColumn[] = [
  // Household
  { id: 'name', header: 'Household', isVisible: true, group: 'Household' },
  { id: 'householdStage', header: 'Stage', isVisible: true, group: 'Household' },
  { id: 'savvyAum', header: 'Billable Assets', isVisible: true, group: 'Household' },
  { id: 'advisorAnnualRevenue', header: 'ARR', isVisible: true, group: 'Household' },
  { id: 'firmAnnualRevenue', header: 'Firm ARR', isVisible: false, group: 'Household' },
  { id: 'serviceCalendar', header: 'Service Calendar', isVisible: true, group: 'Household' },
  { id: 'nextServiceItem', header: 'Next Service Item', isVisible: false, group: 'Household' },
  { id: 'lastContactAt', header: 'Last Contact', isVisible: true, group: 'Household' },
  { id: 'tags', header: 'Tags', isVisible: true, group: 'Household' },
  { id: 'active', header: 'Active', isVisible: true, group: 'Household' },
  { id: 'adviceOnly', header: 'Advice Only', isVisible: false, group: 'Household' },
  { id: 'bio', header: 'Bio', isVisible: false, group: 'Household' },
  { id: 'createdAt', header: 'Created', isVisible: true, group: 'Household' },
  { id: 'updatedAt', header: 'Last Updated', isVisible: true, group: 'Household' },
  { id: 'householdStageUpdatedAt', header: 'Stage Updated', isVisible: false, group: 'Household' },
  // Lead & Compliance
  { id: 'leadSource', header: 'Lead Source', isVisible: true, group: 'Lead & Compliance' },
  { id: 'marketingLeadSource', header: 'Marketing Lead Source', isVisible: false, group: 'Lead & Compliance' },
  { id: 'qualifiedClient', header: 'Qualified Client', isVisible: true, group: 'Lead & Compliance' },
  {
    id: 'qualifiedClientRationale',
    header: 'Qualified Client Rationale',
    isVisible: false,
    group: 'Lead & Compliance',
  },
  { id: 'accreditedInvestor', header: 'Accredited Investor', isVisible: true, group: 'Lead & Compliance' },
  {
    id: 'accreditedInvestorRationale',
    header: 'Accredited Investor Rationale',
    isVisible: false,
    group: 'Lead & Compliance',
  },
  { id: 'qualifiedPurchaser', header: 'Qualified Purchaser', isVisible: false, group: 'Lead & Compliance' },
  {
    id: 'qualifiedPurchaserRationale',
    header: 'Qualified Purchaser Rationale',
    isVisible: false,
    group: 'Lead & Compliance',
  },
  // Primary Client
  { id: 'primaryClientName', header: 'Primary Client', isVisible: true, group: 'Primary Client' },
  {
    id: 'primaryClientPreferredName',
    header: 'Primary Client Preferred Name',
    isVisible: true,
    group: 'Primary Client',
  },
  { id: 'primaryClientEmail', header: 'Primary Client Email', isVisible: true, group: 'Primary Client' },
  { id: 'primaryClientPhone', header: 'Primary Client Phone', isVisible: true, group: 'Primary Client' },
  { id: 'primaryClientDob', header: 'Primary Client DOB', isVisible: true, group: 'Primary Client' },
  { id: 'primaryClientLastLogin', header: 'Primary Client Last Login', isVisible: true, group: 'Primary Client' },
  {
    id: 'primaryClientAddressLine1',
    header: 'Primary Client Address Line 1',
    isVisible: true,
    group: 'Primary Client',
  },
  {
    id: 'primaryClientAddressLine2',
    header: 'Primary Client Address Line 2',
    isVisible: true,
    group: 'Primary Client',
  },
  { id: 'primaryClientCity', header: 'Primary Client City', isVisible: true, group: 'Primary Client' },
  { id: 'primaryClientState', header: 'Primary Client State', isVisible: true, group: 'Primary Client' },
  { id: 'primaryClientPostalCode', header: 'Primary Client Postal Code', isVisible: true, group: 'Primary Client' },
  {
    id: 'primaryClientEmploymentStatus',
    header: 'Primary Client Employment Status',
    isVisible: false,
    group: 'Primary Client',
  },
  { id: 'primaryClientJobTitle', header: 'Primary Client Job Title', isVisible: false, group: 'Primary Client' },
  {
    id: 'primaryClientEmployerName',
    header: 'Primary Client Employer Name',
    isVisible: false,
    group: 'Primary Client',
  },
  { id: 'primaryClientOccupation', header: 'Primary Client Occupation', isVisible: false, group: 'Primary Client' },
  {
    id: 'primaryClientAnnualGrossIncome',
    header: 'Primary Client Annual Gross Income',
    isVisible: false,
    group: 'Primary Client',
  },
  { id: 'primaryClientCitizenship', header: 'Primary Client Citizenship', isVisible: false, group: 'Primary Client' },
  {
    id: 'primaryClientRetirementDate',
    header: 'Primary Client Retirement Date',
    isVisible: false,
    group: 'Primary Client',
  },
  {
    id: 'primaryClientUsCitizenshipStatus',
    header: 'Primary Client US Citizenship Status',
    isVisible: false,
    group: 'Primary Client',
  },
  // Co-Client
  { id: 'coClientName', header: 'Co-Client', isVisible: true, group: 'Co-Client' },
  { id: 'coClientPreferredName', header: 'Co-Client Preferred Name', isVisible: true, group: 'Co-Client' },
  { id: 'coClientEmail', header: 'Co-Client Email', isVisible: true, group: 'Co-Client' },
  { id: 'coClientPhone', header: 'Co-Client Phone', isVisible: true, group: 'Co-Client' },
  { id: 'coClientDob', header: 'Co-Client DOB', isVisible: true, group: 'Co-Client' },
  { id: 'coClientLastLogin', header: 'Co-Client Last Login', isVisible: true, group: 'Co-Client' },
  { id: 'coClientAddressLine1', header: 'Co-Client Address Line 1', isVisible: true, group: 'Co-Client' },
  { id: 'coClientAddressLine2', header: 'Co-Client Address Line 2', isVisible: true, group: 'Co-Client' },
  { id: 'coClientCity', header: 'Co-Client City', isVisible: true, group: 'Co-Client' },
  { id: 'coClientState', header: 'Co-Client State', isVisible: true, group: 'Co-Client' },
  { id: 'coClientPostalCode', header: 'Co-Client Postal Code', isVisible: true, group: 'Co-Client' },
  {
    id: 'coClientEmploymentStatus',
    header: 'Co-Client Employment Status',
    isVisible: false,
    group: 'Co-Client',
  },
  { id: 'coClientJobTitle', header: 'Co-Client Job Title', isVisible: false, group: 'Co-Client' },
  { id: 'coClientEmployerName', header: 'Co-Client Employer Name', isVisible: false, group: 'Co-Client' },
  { id: 'coClientOccupation', header: 'Co-Client Occupation', isVisible: false, group: 'Co-Client' },
  {
    id: 'coClientAnnualGrossIncome',
    header: 'Co-Client Annual Gross Income',
    isVisible: false,
    group: 'Co-Client',
  },
  { id: 'coClientCitizenship', header: 'Co-Client Citizenship', isVisible: false, group: 'Co-Client' },
  { id: 'coClientRetirementDate', header: 'Co-Client Retirement Date', isVisible: false, group: 'Co-Client' },
  {
    id: 'coClientUsCitizenshipStatus',
    header: 'Co-Client US Citizenship Status',
    isVisible: false,
    group: 'Co-Client',
  },
  // Family
  { id: 'familyInfo', header: 'Family Info', isVisible: false, group: 'Family' },
  // Billing
  { id: 'feeScheduleName', header: 'Fee Schedule', isVisible: true, group: 'Billing' },
  { id: 'feeType', header: 'Fee Type', isVisible: true, group: 'Billing' },
  { id: 'amountType', header: 'Amount Type', isVisible: true, group: 'Billing' },
  { id: 'flatFeeAmount', header: 'Flat Fee Amount', isVisible: true, group: 'Billing' },
  { id: 'minimumFee', header: 'Minimum Fee', isVisible: true, group: 'Billing' },
  { id: 'csaName', header: 'CSA', isVisible: false, group: 'Billing' },
  { id: 'contractSignatureDate', header: 'Contract Signature Date', isVisible: false, group: 'Billing' },
  { id: 'advisorName', header: 'Primary', isVisible: true, group: 'Billing' },
] as const;

const DEFAULT_SELECTED_COLUMN_IDS = HOUSEHOLD_EXPORT_COLUMNS.filter((c) => c.isVisible).map((c) => c.id);

// HouseholdExporter only populates these under flagServiceCalendarV2; offering
// them on the legacy calendar would export a blank column.
const SERVICE_CALENDAR_V2_COLUMN_IDS: readonly string[] = ['nextServiceItem'] as const;

const FORMATS: readonly ExportFormat[] = ['csv'] as const;

/**
 * The advisor-dashboard filter state we thread into the export so the CSV matches
 * the on-screen filtered list. These are encoded as entries in
 * the generic `filters` argument by `buildDashboardFilterEntries`; the backend's
 * HouseholdExporter is the only place that interprets them. This is intentionally
 * the subset of dashboard filters the clients table varies — not a 1:1 mirror of
 * any backend input object.
 */
export type HouseholdDashboardFilters = {
  readonly advisorIds?: readonly string[] | null;
  readonly excludedAdvisorIds?: readonly string[] | null;
  readonly reviewDue?: boolean | null;
  readonly servicesDue?: boolean | null;
  readonly householdTags?: readonly string[] | null;
  readonly excludedHouseholdTags?: readonly string[] | null;
  readonly householdTagCategories?: readonly string[] | null;
  readonly excludedHouseholdTagCategories?: readonly string[] | null;
  readonly primaryAddressStates?: readonly string[] | null;
  readonly excludedPrimaryAddressStates?: readonly string[] | null;
  readonly lastContactDaysSince?: readonly number[] | null;
  readonly excludedLastContactDaysSince?: readonly number[] | null;
};

type UseHouseholdExportParams = {
  readonly globalFilter?: string | null;
  readonly dashboardFilters?: HouseholdDashboardFilters | null;
};

export type UseHouseholdExportReturn = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly columns: ExportableColumn[];
  readonly formats: ExportFormat[];
  readonly selectedColumnIds: string[];
  readonly selectedFormat: ExportFormat;
  readonly state: ExportState;
  readonly availableColumns: ExportableColumn[];
  readonly selectedColumns: ExportableColumn[];
  readonly canExport: boolean;
  readonly setSelectedFormat: (format: ExportFormat) => void;
  readonly toggleColumn: (columnId: string) => void;
  readonly addColumn: (columnId: string) => void;
  readonly removeColumn: (columnId: string) => void;
  readonly reset: () => void;
  readonly execute: () => Promise<void>;
  readonly setModalOpen: (open: boolean) => void;
  readonly applyDashboardFilters: boolean;
  readonly setApplyDashboardFilters: (next: boolean) => void;
  readonly reorderColumns: (nextColumnIds: string[]) => void;
  readonly savedPresets: SavedExportPreset[];
  readonly activePresetId: string | null;
  readonly isPresetModified: boolean;
  readonly presetsAtLimit: boolean;
  readonly loadPreset: (id: string) => void;
  readonly clearActivePreset: () => void;
  readonly savePreset: (name: string) => void;
  readonly renamePreset: (id: string, name: string) => void;
  readonly deletePreset: (id: string) => void;
};

export function useHouseholdExport(params: UseHouseholdExportParams): UseHouseholdExportReturn {
  const { globalFilter, dashboardFilters } = params;
  const { flags } = useFlagsAndLoadingState();
  const flagServiceCalendarV2 = Boolean(flags['flagServiceCalendarV2']);

  const exportColumns = useMemo(
    () =>
      flagServiceCalendarV2
        ? [...HOUSEHOLD_EXPORT_COLUMNS]
        : HOUSEHOLD_EXPORT_COLUMNS.filter((col) => !SERVICE_CALENDAR_V2_COLUMN_IDS.includes(col.id)),
    [flagServiceCalendarV2],
  );

  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(() => [...DEFAULT_SELECTED_COLUMN_IDS]);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [state, setState] = useState<ExportState>({ status: 'idle', error: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [exportIdToPoll, setExportIdToPoll] = useState<string | null>(null);
  // Default-on: opening the dialog with active filters, the advisor expects the
  // export to honour the filtered set without ticking anything extra.
  const [applyDashboardFilters, setApplyDashboardFilters] = useState(true);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const executeDeferredRef = useRef<DeferredPromise | null>(null);

  const {
    savedPresets,
    atLimit: presetsAtLimit,
    savePreset,
    renamePreset,
    deletePreset,
  } = useSavedHouseholdExportPresets();

  const [createDataExport] = useMutationWithErrorLogging(CreateDataExportMutation);

  const { data: pollData, stopPolling } = useQueryWithPolling(DataExportQuery, {
    skip: isNil(exportIdToPoll),
    variables: { id: exportIdToPoll ?? '' },
    pollInterval: !isNil(exportIdToPoll) ? POLL_INTERVAL_MS : 0,
    maxPollTime: MAX_POLL_TIME_MS,
    onPollingExpired: () => {
      setExportIdToPoll(null);
      const msg = 'Export is taking longer than expected. Please try again later.';
      setState({ status: 'error', error: msg });
      executeDeferredRef.current?.reject(new Error(msg));
      executeDeferredRef.current = null;
    },
  });

  const availableColumns = useMemo(
    () => exportColumns.filter((col) => !selectedColumnIds.includes(col.id)),
    [exportColumns, selectedColumnIds],
  );

  const selectedColumns = useMemo(
    () =>
      selectedColumnIds
        .map((id) => exportColumns.find((c) => c.id === id))
        .filter((col): col is ExportableColumn => !isNil(col)),
    [exportColumns, selectedColumnIds],
  );

  const canExport = selectedColumnIds.length > 0 && state.status !== 'exporting';

  const toggleColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  }, []);

  const addColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
  }, []);

  const removeColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => prev.filter((id) => id !== columnId));
  }, []);

  const reorderColumns = useCallback((nextColumnIds: string[]) => {
    setSelectedColumnIds((prev) => {
      const prevIds = new Set(prev);
      const isReorderOfSelection = nextColumnIds.length === prev.length && nextColumnIds.every((id) => prevIds.has(id));
      return isReorderOfSelection ? nextColumnIds : prev;
    });
  }, []);

  const reset = useCallback(() => {
    // Settle any in-flight execute() promise before clearing exportIdToPoll - once
    // it's null the poll-result effect below bails out early and would otherwise
    // never resolve/reject the promise execute() is awaiting, leaving that call
    // hanging forever even though the backend export may still complete. Resolve
    // (not reject) so execute()'s catch block doesn't clobber the idle state this
    // function is about to set with a confusing "export failed" error.
    if (executeDeferredRef.current !== null) {
      executeDeferredRef.current.resolve();
      executeDeferredRef.current = null;
    }
    setSelectedColumnIds([...DEFAULT_SELECTED_COLUMN_IDS]);
    setSelectedFormat('csv');
    setState({ status: 'idle', error: null });
    setExportIdToPoll(null);
    setApplyDashboardFilters(true);
    setActivePresetId(null);
  }, []);

  const columnMap = useMemo(() => new Map(exportColumns.map((col) => [col.id, col])), [exportColumns]);

  // Compare against the preset's columnIds filtered through columnMap (not the raw
  // stored array) so a preset with since-retired column ids doesn't show as
  // "modified" immediately after a clean load - loadPreset applies the same filter.
  // Column order matters here (unlike e.g. tags' unordered-set comparisons), since
  // reordering is one of the things a preset can drift from - compare index-by-index.
  const isPresetModified = useMemo(() => {
    if (isNil(activePresetId)) return false;
    const preset = savedPresets.find((p) => p.id === activePresetId);
    if (isNil(preset)) return false;
    if (preset.filters.applyDashboardFilters !== applyDashboardFilters) return true;
    const expectedFormat = FORMATS.includes(preset.filters.format) ? preset.filters.format : (FORMATS[0] ?? 'csv');
    if (expectedFormat !== selectedFormat) return true;
    const expectedColumnIds = preset.filters.columnIds.filter((columnId) => columnMap.has(columnId));
    if (expectedColumnIds.length !== selectedColumnIds.length) return true;
    return expectedColumnIds.some((id, index) => id !== selectedColumnIds[index]);
  }, [activePresetId, savedPresets, selectedFormat, selectedColumnIds, columnMap, applyDashboardFilters]);

  const loadPreset = useCallback(
    (id: string) => {
      const preset = savedPresets.find((p) => p.id === id);
      if (isNil(preset)) return;

      const validColumnIds = preset.filters.columnIds.filter((columnId) => columnMap.has(columnId));
      if (validColumnIds.length === 0) return; // every saved column id is stale - nothing to load
      setSelectedColumnIds(validColumnIds);
      setSelectedFormat(FORMATS.includes(preset.filters.format) ? preset.filters.format : (FORMATS[0] ?? 'csv'));
      setApplyDashboardFilters(preset.filters.applyDashboardFilters);
      setActivePresetId(id);
    },
    [savedPresets, columnMap],
  );

  // Deliberately narrow: only unlinks the active preset. Unlike reset(), this must
  // not touch column/format selection or export state, since SavedViews wires this
  // to the active chip's own click handler - it can fire while an export is polling,
  // and reset() clearing exportIdToPoll there would strand the in-flight execute()
  // promise (the poll-result effect below bails out once exportIdToPoll is null).
  const clearActivePreset = useCallback(() => {
    setActivePresetId(null);
  }, []);

  const handleSavePreset = useCallback(
    (name: string) => {
      const created = savePreset(name, { columnIds: selectedColumnIds, format: selectedFormat, applyDashboardFilters });
      if (!isNil(created)) setActivePresetId(created.id);
    },
    [savePreset, selectedColumnIds, selectedFormat, applyDashboardFilters],
  );

  const handleDeletePreset = useCallback(
    (id: string) => {
      const remaining = deletePreset(id);
      if (activePresetId === id || remaining.length === 0) setActivePresetId(null);
    },
    [deletePreset, activePresetId],
  );

  useEffect(() => {
    if (isNil(pollData?.dataExport) || isNil(exportIdToPoll)) return;

    const exportRow = pollData.dataExport;
    const status = exportRow.status;

    if (status === 'completed') {
      setExportIdToPoll(null);
      stopPolling();

      const downloadUrl = exportRow.downloadUrl;
      const filename = exportRow.filename;

      if (!isNil(downloadUrl) && downloadUrl !== '') {
        downloadWithAuth(downloadUrl, filename)
          .then(() => {
            setState({ status: 'success', error: null });
            setModalOpen(false);
            executeDeferredRef.current?.resolve();
            executeDeferredRef.current = null;
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Download failed. Please try again.';
            setState({ status: 'error', error: message });
            executeDeferredRef.current?.reject(err instanceof Error ? err : new Error(message));
            executeDeferredRef.current = null;
          });
      } else {
        setState({ status: 'success', error: null });
        setModalOpen(false);
        executeDeferredRef.current?.resolve();
        executeDeferredRef.current = null;
      }
      return;
    }

    if (status === 'failed') {
      setExportIdToPoll(null);
      stopPolling();
      const errorMessage = exportRow.errorMessage ?? 'Export failed. Please try again.';
      setState({ status: 'error', error: errorMessage });
      executeDeferredRef.current?.reject(new Error(errorMessage));
      executeDeferredRef.current = null;
    }
  }, [pollData, exportIdToPoll, stopPolling]);

  const execute = useCallback(async () => {
    if (selectedColumnIds.length === 0 || state.status === 'exporting') return;

    setState({ status: 'exporting', error: null });

    const deferredPromise = new Promise<void>((resolve, reject) => {
      executeDeferredRef.current = { resolve, reject };
    });

    const filters = applyDashboardFilters ? buildDashboardFilterEntries(dashboardFilters) : [];

    try {
      const result = await createDataExport({
        variables: {
          resourceType: 'households',
          format: selectedFormat,
          columnIds: selectedColumnIds,
          filters,
          sorting: [],
          globalFilter: !isNil(globalFilter) && globalFilter !== '' ? globalFilter : null,
        },
      });

      const dataExport = result.data?.createDataExport?.dataExport;
      const errors = result.data?.createDataExport?.errors;

      if (!isNil(errors) && errors.length > 0) {
        const msg = errors[0] ?? 'Export failed.';
        setState({ status: 'error', error: msg });
        executeDeferredRef.current?.reject(new Error(msg));
        executeDeferredRef.current = null;
        throw new Error(msg);
      }

      if (isNil(dataExport?.id)) {
        const msg = 'Export could not be started. Please try again.';
        setState({ status: 'error', error: msg });
        executeDeferredRef.current?.reject(new Error(msg));
        executeDeferredRef.current = null;
        throw new Error(msg);
      }

      setExportIdToPoll(dataExport.id);
      await deferredPromise;
    } catch (err) {
      setState({
        status: 'error',
        error: err instanceof Error ? err.message : 'Export failed. Please try again.',
      });
      executeDeferredRef.current = null;
      throw err;
    }
  }, [
    selectedColumnIds,
    selectedFormat,
    state.status,
    globalFilter,
    applyDashboardFilters,
    dashboardFilters,
    createDataExport,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (open) {
        reset();
      }
    },
    [reset],
  );

  return {
    open: modalOpen,
    onOpenChange: handleOpenChange,
    columns: exportColumns,
    formats: [...FORMATS],
    selectedColumnIds,
    selectedFormat,
    state,
    availableColumns,
    selectedColumns,
    canExport,
    setSelectedFormat,
    toggleColumn,
    addColumn,
    removeColumn,
    reset,
    execute,
    setModalOpen,
    applyDashboardFilters,
    setApplyDashboardFilters,
    reorderColumns,
    savedPresets,
    activePresetId,
    isPresetModified,
    presetsAtLimit,
    loadPreset,
    clearActivePreset,
    savePreset: handleSavePreset,
    renamePreset,
    deletePreset: handleDeletePreset,
  };
}

function nonEmptyOrNull<T>(value: readonly T[] | null | undefined): T[] | null {
  if (isNil(value) || value.length === 0) return null;
  return [...value];
}

// Entries for the generic `filters` mutation argument (TanStack-table shape:
// `{ id, value: { <type>: { operator, ... } } }`). The HouseholdExporter decodes
// these ids back into the dashboard's filter semantics.
type ExportFilterEntry = {
  readonly id: string;
  readonly value:
    | { readonly select: { readonly operator: 'ANY_OF' | 'NONE_OF'; readonly values: string[] } }
    | { readonly checkbox: { readonly operator: 'IS'; readonly value: boolean } };
};

function selectFilterEntries(
  id: string,
  included: readonly (string | number)[] | null | undefined,
  excluded: readonly (string | number)[] | null | undefined,
): ExportFilterEntry[] {
  const entries: ExportFilterEntry[] = [];
  const includedValues = nonEmptyOrNull(included);
  if (!isNil(includedValues)) {
    entries.push({ id, value: { select: { operator: 'ANY_OF', values: includedValues.map(String) } } });
  }
  const excludedValues = nonEmptyOrNull(excluded);
  if (!isNil(excludedValues)) {
    entries.push({ id, value: { select: { operator: 'NONE_OF', values: excludedValues.map(String) } } });
  }
  return entries;
}

function checkboxFilterEntry(id: string, value: boolean | null | undefined): ExportFilterEntry | null {
  if (isNil(value)) return null;
  return { id, value: { checkbox: { operator: 'IS', value } } };
}

export function buildDashboardFilters(clientsUrl: ClientsUrlState): HouseholdDashboardFilters {
  return {
    advisorIds: clientsUrl.advisor,
    excludedAdvisorIds: clientsUrl.advisorExcluded,
    reviewDue: clientsUrl.reviewDue,
    servicesDue: clientsUrl.servicesDue,
    householdTags: clientsUrl.householdTags,
    excludedHouseholdTags: clientsUrl.householdTagsExcluded,
    householdTagCategories: clientsUrl.householdTagCategories,
    excludedHouseholdTagCategories: clientsUrl.householdTagCategoriesExcluded,
    primaryAddressStates: clientsUrl.primaryAddressStates,
    excludedPrimaryAddressStates: clientsUrl.primaryAddressStatesExcluded,
    lastContactDaysSince: clientsUrl.lastContactDaysSince
      ?.map((value) => Number.parseInt(value, 10))
      .filter((value) => !Number.isNaN(value)),
    excludedLastContactDaysSince: clientsUrl.lastContactDaysSinceExcluded
      ?.map((value) => Number.parseInt(value, 10))
      .filter((value) => !Number.isNaN(value)),
  };
}

/**
 * Encodes the dashboard filter state as generic `filters` entries. The stage
 * toggles are always emitted so the export restricts to the dashboard's default
 * (Won) household-stage scope even when no other filter is active — matching what
 * the advisor sees on screen.
 */
function buildDashboardFilterEntries(filters: HouseholdDashboardFilters | null | undefined): ExportFilterEntry[] {
  return [
    ...selectFilterEntries('advisorIds', filters?.advisorIds, filters?.excludedAdvisorIds),
    ...selectFilterEntries('householdTags', filters?.householdTags, filters?.excludedHouseholdTags),
    ...selectFilterEntries(
      'householdTagCategories',
      filters?.householdTagCategories,
      filters?.excludedHouseholdTagCategories,
    ),
    ...selectFilterEntries(
      'primaryAddressStates',
      filters?.primaryAddressStates,
      filters?.excludedPrimaryAddressStates,
    ),
    ...selectFilterEntries(
      'lastContactDaysSince',
      filters?.lastContactDaysSince,
      filters?.excludedLastContactDaysSince,
    ),
    checkboxFilterEntry('reviewDue', filters?.reviewDue),
    checkboxFilterEntry('servicesDue', filters?.servicesDue),
    checkboxFilterEntry('includeProspectStages', false),
    checkboxFilterEntry('inactiveOnly', false),
  ].filter((entry): entry is ExportFilterEntry => !isNil(entry));
}
